import * as imgUtil from "@shared/imgUtil.js";
import { renderSettings, croppedDrawRect } from "@shared/RenderSettings.js";
import { Product } from "@shared/Product.js";
import { CYODesign } from "@shared/CYODesign.js";
import { BorderLoc } from "@shared/BorderLoc.js";
import { Edge } from "@shared/BorderLoc.js";
import { arraysUnorderedEqual, arraysEqual } from "@shared/BorderLoc.js";
import { Rect } from "@shared/Rect.js";
import { ArrayUtil } from "@shared/utility.js";
import { RandomState } from "@shared/RandomState.js";
import { ContentRendererUtils } from "@shared/ContentRendererUtils.js";
import { dlog } from "@shared/utility.js";

const MIN_CORNER_STICKER_RESOLUTION = 22000;
const SMALL_USER_PHOTO_FOR_BORDER_HEIGHT = 220;

export class ContentRendererBorderCorner {
    
    static drawBorder(
        stickersCtx,
        photoStickersCtx,
        userImgVariant,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        stickerImagesUnique,
        popSticker,
        estimateStickerBorderHeight,
        estimateUserImageBorderHeight,
        randomState = null
    ) {
        const product = device.productCategory;

        if (!(placements.someBorder || placements.someCorner)) {
            return { stickerImagesUnique, smallUserPhotoForBorder: null };
        }

        dlog(">> _drawBorder");

        if (placements.photo.corner && cyoDesign.stickerSet == null) {
            // Simple case: draw a single user image sticker in just one corner (CYO-3162)
            const borderLocs = BorderLoc.getCornerLocs(device, canvasWidth, canvasHeight, false);
            const cornerLoc = BorderLoc.randomBottomCornerLoc(borderLocs, null, `_drawBorder-${product}`, randomState);
            ContentRendererBorderCorner.drawSingleCornerUserImage(photoStickersCtx, userImgVariant, cornerLoc, device, canvasWidth, canvasHeight, cyoDesign);
        } else if (placements.photo.corner && placements.stickers.corner) {
            const result = ContentRendererBorderCorner.drawImgAndStickerCorners(
                stickersCtx, photoStickersCtx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign, stickerImages, placements, popSticker, randomState
            );
            return result;
        } else if (placements.photo.corner && (placements.stickers.border || placements.stickers.pattern)) {
            const result = ContentRendererBorderCorner.drawImgCornerAndStickerBorderOrPattern(
                stickersCtx, photoStickersCtx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign, stickerImages, placements, stickerImagesUnique, popSticker, randomState
            );
            return result;
        } else {
            const result = ContentRendererBorderCorner.drawBorderImgAndOrStickers(
                stickersCtx, photoStickersCtx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign, stickerImages, placements, stickerImagesUnique, popSticker, estimateStickerBorderHeight, estimateUserImageBorderHeight, randomState
            );
            return result;
        }

        return { stickerImagesUnique, smallUserPhotoForBorder: null };
    }
    
    static drawCorner(
        stickersCtx,
        photoStickersCtx,
        userImgVariant,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        stickerImagesUnique,
        popSticker,
        randomState = null
    ) {
        const product = device.productCategory;

        if (!placements.someCorner) {
            return { stickerImagesUnique };
        }

        dlog(">> _drawCorner");
        
        if (placements.photo.corner && cyoDesign.stickerSet == null) {
            // Simple case: draw a single user image sticker in just one corner (CYO-3162)
            const borderLocs = BorderLoc.getCornerLocs(device, canvasWidth, canvasHeight);
            const cornerLoc = BorderLoc.randomBottomCornerLoc(borderLocs, null, `_drawCorner-${product}`, randomState);
            ContentRendererBorderCorner.drawSingleCornerUserImage(photoStickersCtx, userImgVariant, cornerLoc, device, canvasWidth, canvasHeight, cyoDesign);
        } else if (placements.photo.corner && placements.stickers.corner) {
            const result = ContentRendererBorderCorner.drawImgAndStickerCorners(
                stickersCtx, photoStickersCtx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign, stickerImages, placements, popSticker, randomState
            );
            return result;
        } else if (placements.photo.corner && (placements.stickers.border || placements.stickers.pattern)) {
            const result = ContentRendererBorderCorner.drawImgCornerAndStickerBorderOrPattern(
                stickersCtx, photoStickersCtx, userImgVariant, device, canvasWidth, canvasHeight, cyoDesign, stickerImages, placements, stickerImagesUnique, popSticker, randomState
            );
            return result;
        } else {
            const result = ContentRendererBorderCorner.drawCornerStickersOnly(
                stickersCtx, device, canvasWidth, canvasHeight, cyoDesign, stickerImages, placements, popSticker, randomState
            );
            return result;
        }

        return { stickerImagesUnique };
    }
    
    static drawBorderImgAndOrStickers(
        stickersCtx,
        photoStickersCtx,
        userImgVariant,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        stickerImagesUnique,
        popSticker,
        estimateStickerBorderHeight,
        estimateUserImageBorderHeight,
        randomState = null
    ) {
        const product = device.productCategory;

        if (!placements.someBorder) {
            return { stickerImagesUnique, smallUserPhotoForBorder: null };
        }
        
        dlog(">> _drawBorderImgAndOrStickers");

        var borderLocs;
        if (product == Product.grip && placements.someCorner && !placements.someBorder) {
            // Special case: get corners only
            // Ignoring margins looks bad. See #160: Grip: photo/center + sticker/corners, stickers inset too much.
            borderLocs = BorderLoc.getCornerLocs(device, canvasWidth, canvasHeight, false);
        } else {
            let estimatedStickerHeight;
            if (cyoDesign.stickerSet && stickerImages.length > 0) {
                estimatedStickerHeight = estimateStickerBorderHeight();
            } else {
                estimatedStickerHeight = estimateUserImageBorderHeight();
            }
            borderLocs = BorderLoc.getLocs(device, canvasWidth, canvasHeight, estimatedStickerHeight);
        }

        let borderCounter = 0;
        let smallUserPhotoForBorder = null; // _drawNextBorderSticker caches this
        
        borderLocs.forEach((nextLoc) => {
            if (stickerImagesUnique.length == 0) {
                // re-populate unique sticker images
                stickerImagesUnique = stickerImages.concat();
            }
        
            if (placements.photo.corner && nextLoc.isCorner()) {
                ContentRendererBorderCorner.drawSingleCornerUserImage(photoStickersCtx, userImgVariant, nextLoc, device, canvasWidth, canvasHeight, cyoDesign);
            } else {
                // Custom avoidance for case
                if (product == Product.case) {
                    if (placements.stickers.border && placements.photo.corner) {
                        // If border stickers and corner user img, skip this sticker
                        if (nextLoc.hasEdge(Edge.right) && nextLoc.y <= 120) {
                            return;
                        }
                    } else if (placements.stickers.corner || placements.photo.corner) {
                        if (nextLoc.hasEdges([Edge.top, Edge.center])) {
                            // When placing just a top-right corner on the case, we need to shift it over since it was originally in the center.
                            nextLoc.x += canvasWidth * 0.15;
                        }
                    }
                }
                const result = ContentRendererBorderCorner.drawNextBorderSticker(
                    stickersCtx, photoStickersCtx, userImgVariant, nextLoc, [], device, canvasWidth, canvasHeight, cyoDesign, placements, stickerImagesUnique, borderCounter, smallUserPhotoForBorder, popSticker, randomState
                );
                stickerImagesUnique = result.stickerImagesUnique;
                borderCounter = result.borderCounter;
                smallUserPhotoForBorder = result.smallUserPhotoForBorder;
            }
        });

        return { stickerImagesUnique, smallUserPhotoForBorder };
    }
    
    static drawCornerStickersOnly(
        stickersCtx,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign,
        stickerImages, 
        placements,
        popSticker,
        randomState = null
    ) {
        const product = device.productCategory;
        const stickerRotationRandomness = renderSettings(product, "borderRotationRandomnessDeg");
        const smallCornerInset = renderSettings(product, "cornerSmallStickerInsetRatio") * canvasWidth;
        
        var sticker_scale = renderSettings(product, "borderStickerScale");
        const f = cyoDesign.stickerSet.borderStickerScale?.[product];
        if (f) {
            sticker_scale = f;
        }
        sticker_scale *= (device.stickerScaleFactor || 1.0);

        dlog(">> _drawCornerStickersOnly");

        const cornerLocs = BorderLoc.getCornerLocs(device, canvasWidth, canvasHeight, false);

        // Find suitable stickers to draw
        var cornerStickers = [];
        while (cornerStickers.length < cornerLocs.length) {
            const [, stickerImg] = popSticker(sticker => sticker.width * sticker.height >= MIN_CORNER_STICKER_RESOLUTION);
            if (!stickerImg) { continue; }
            cornerStickers.push(stickerImg);
        }

        if (cornerStickers.length != cornerLocs.length) {
            throw new Error("cornerStickers and cornerLocs lengths do not match");
        }
        
        var drawIdx = 0;
        
        // A lot of this logic is very similar to _drawNextBorderSticker. Can they be combined?
        for (var nextLoc of cornerLocs) {
            const nextPoint = nextLoc.pt();
            const stickerImg = cornerStickers[drawIdx];
            
            if (product == Product.case) {
                if (nextLoc.hasEdges([Edge.top, Edge.center])) {
                    // When placing just a top-right corner on the case, we need to shift it over since it was originally in the center.
                    nextLoc.x += canvasWidth * 0.15;
                }
            }
            
            // Scale relative to canvas height (the dominant dimension for all products) to maintain consistent sizing
            const imgScale = sticker_scale * canvasHeight / Math.max(stickerImg.height, stickerImg.width);

            // rotate and place onto canvas
            const rotationAngle = (cyoDesign.debugOptions.disableRotations || cyoDesign.stickerSet?.disableRotations)
                ? 0
                : imgUtil.linearRandom(-stickerRotationRandomness, stickerRotationRandomness, randomState) * Math.PI / 180;
            
            
            const drawOutline = cyoDesign.stickerOutlines;
            
            const rotCanvas = imgUtil.rotatedCanvas(stickerImg, rotationAngle);
            const rotationHDif = imgScale * (rotCanvas.height - stickerImg.height);
            
            const drawRect = new Rect();
            drawRect.width = rotCanvas.width * imgScale;
            drawRect.height = rotCanvas.height * imgScale;            
            
            dlog(`width: ${drawRect.width}; height: ${drawRect.height}`)
            
            drawRect.x = nextLoc.ptX();
            drawRect.y = nextLoc.ptY();
            
            // Adjustments…
            
            // If along left or right edge, inset up to compensate for rotation rect size diff
            if (nextLoc.isEdge(Edge.right) || nextLoc.isEdge(Edge.left)) {
                drawRect.y -= rotationHDif/2;
            }
            
            if (product == Product.grip) {
                // Adjust stickers on grip so they're centered
                drawRect.x -= drawRect.width/2;
                drawRect.y -= drawRect.height/2;
            } else /* case & wallet */ {
                // If along right, inset left to compensate for rotation rect size diff (case & wallet)
                if (nextLoc.hasEdge(Edge.right)) {
                    // We're drawing on the right edge; inset left
                    drawRect.x -= drawRect.width;
                }
                // If drawing along the bottom, inset up to its visible
                if (nextLoc.hasEdge(Edge.bottom)) {
                    drawRect.y -= drawRect.height;
                }
                // On case bottom middle, move left to compensate for BorderLoc.getLocs issue
                if (nextLoc.hasEdges([Edge.bottom, Edge.center]) && product == Product.case) {
                    drawRect.x -= drawRect.width/2;
                }                
            }
            
            // Apply cornerSmallStickerInsetRatio
            if (placements.stickers.corner) {
                drawRect.insetOriginBy(
                    [nextLoc.hasEdge(Edge.top) ? smallCornerInset : 0,
                    nextLoc.hasEdge(Edge.right) ? smallCornerInset : 0,
                    nextLoc.hasEdge(Edge.bottom) ? smallCornerInset : 0,
                    nextLoc.hasEdge(Edge.left) ? smallCornerInset : 0
                    ])
            }
                                    
            if (cyoDesign.debugOptions.drawBgs) { 
                ContentRendererUtils.debugAddBg(rotCanvas, ContentRendererUtils.debugBgColor2);
            }
            
            ContentRendererUtils.placeImage(stickersCtx, 
                rotCanvas,
                drawRect,
                drawOutline);

            drawIdx++;
        }

        return { stickerImagesUnique: [] };
    }
        
    static drawNextBorderSticker(
        stickersCtx,
        photoStickersCtx,
        userImgVariant,
        nextLoc, 
        avoidRects,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign,
        placements,
        stickerImagesUnique,
        borderCounter,
        smallUserPhotoForBorder,
        popSticker,
        randomState = null
    ) {
        // NB: for the photo this is only ever called when the photo is set to border.
        const product = device.productCategory;

        const nextPoint = nextLoc.pt();
        const stickerRotationRandomness = renderSettings(product, "borderRotationRandomnessDeg");
        const smallCornerInset = renderSettings(product, "cornerSmallStickerInsetRatio") * canvasWidth;

        dlog(">> _drawNextBorderSticker");

        // dlog(`nextPoint=${nextPoint}, i=${i}`)        
        let img, idx;
        
        if (userImgVariant && smallUserPhotoForBorder == null) {
            smallUserPhotoForBorder = imgUtil.resizeImageOrCanvasForHeight(userImgVariant, SMALL_USER_PHOTO_FOR_BORDER_HEIGHT);
            if (product == Product.grip) {
                // Also need to mask with circle since that was skipped for grip in _setUpUserImage
                const radius = smallUserPhotoForBorder.width / 2;
                smallUserPhotoForBorder = imgUtil.maskImageWithCircle(smallUserPhotoForBorder, radius);
            }
        }
        
        if (placements.stickers.corner && !placements.photo.corner) {
            // first see if we should place a corner image     
            if (nextLoc.isCorner() && (placements.someCorner)) {
                if (!placements.stickers.corner) {
                    // use uploaded image if first corner (TODO: alternate corners) or no stickers assigned to corners
                    img = smallUserPhotoForBorder;
                    borderCounter = 0; // reset interval counter (should wait another n stickers to place again along border)
                } else {
                    [idx, img] = popSticker(sticker => sticker.width * sticker.height >= MIN_CORNER_STICKER_RESOLUTION);
                }
            } else {
                if (placements.photo.border && (!placements.stickers.border || (borderCounter % 3) == 0)) {
                    // use uploaded image if no border stickers or counter hits the interval
                    img = smallUserPhotoForBorder;
                }
            }
        } else {
            // For #165: Photo + stickers + border: weird alternation on grip and wallet
            const forceSticker = (product == Product.grip && borderCounter == 9);
            
            if ((placements.photo.border && (!placements.stickers.border || (borderCounter % 3) == 0)) 
                && !forceSticker)
            {
                // use uploaded image if no border stickers or counter hits the interval
                img = smallUserPhotoForBorder;
            } else if (placements.stickers.border) {
                [idx, img] = popSticker(sticker => sticker.width * sticker.height >= MIN_CORNER_STICKER_RESOLUTION);
            }
        }
                
        if (img != null) {
            const isUserImage = img == smallUserPhotoForBorder;
            
            var sticker_scale = renderSettings(product, "borderStickerScale");
            if (!isUserImage) {
                // #169: Add some way to control sticker scale on a per-set basis
                const f = cyoDesign.stickerSet.borderStickerScale?.[product];
                if (f) {
                    sticker_scale = f;
                }
            }
            sticker_scale *= (device.stickerScaleFactor || 1.0);

            // place image
            if (isUserImage && placements.photo.corner) {
                // #131: Img = corner, stickers = border, img should still be large
                sticker_scale *= 1.5; // make uploaded image larger
            } else {
                // image is a sticker - get rid of this in the array that keeps track of unique stickers
                stickerImagesUnique.splice(idx, 1);
            }
        
            // override testing; delete soon
            // sticker_scale = 0.16;
        
            dlog(`sticker_scale: ${sticker_scale}`);
            
            // Scale relative to canvas height (the dominant dimension for all products) to maintain consistent sizing
            const imgScale = sticker_scale * canvasHeight / Math.max(img.height, img.width);

            // rotate and place onto canvas
            // #174 disableRotations should apply to user image too (border/pattern placements only)
            const rotationAngle = (cyoDesign.debugOptions.disableRotations || cyoDesign.stickerSet?.disableRotations)
                ? 0
                : imgUtil.linearRandom(-stickerRotationRandomness, stickerRotationRandomness, randomState) * Math.PI / 180;

            const drawOutline = (isUserImage) ? cyoDesign.subjectOutlines : cyoDesign.stickerOutlines;
            
            const rotCanvas = imgUtil.rotatedCanvas(img, rotationAngle);
            const rotationHDif = imgScale * (rotCanvas.height - img.height);
            
            const drawRect = new Rect();
            drawRect.width = rotCanvas.width * imgScale;
            drawRect.height = rotCanvas.height * imgScale;            
        
            dlog(`width: ${drawRect.width}; height: ${drawRect.height}`)
        
            drawRect.x = ArrayUtil.x(nextPoint);
            drawRect.y = ArrayUtil.y(nextPoint);
            
            // Adjustments…

            // If along left or right edge, inset up to compensate for rotation rect size diff
            if (nextLoc.isEdge(Edge.right) || nextLoc.isEdge(Edge.left)) {
                drawRect.y -= rotationHDif/2;
            }

            if (product == Product.grip) {
                // Adjust stickers on grip so they're centered
                drawRect.x -= drawRect.width/2;
                drawRect.y -= drawRect.height/2;
            } else /* case & wallet */ {
                // If along right, inset left to compensate for rotation rect size diff (case & wallet)
                if (nextLoc.hasEdge(Edge.right)) {
                    // We're drawing on the right edge; inset left
                    drawRect.x -= drawRect.width;
                }
                // If drawing along the bottom, inset up to its visible
                if (nextLoc.hasEdge(Edge.bottom)) {
                    drawRect.y -= drawRect.height;
                }
                // On case bottom middle, move left to compensate for BorderLoc.getLocs issue
                if (nextLoc.hasEdges([Edge.bottom, Edge.center]) && product == Product.case) {
                    drawRect.x -= drawRect.width/2;
                }                
            }

            // Apply cornerSmallStickerInsetRatio
            if (placements.stickers.corner) {
                drawRect.insetOriginBy(
                    [nextLoc.hasEdge(Edge.top) ? smallCornerInset : 0,
                    nextLoc.hasEdge(Edge.right) ? smallCornerInset : 0,
                    nextLoc.hasEdge(Edge.bottom) ? smallCornerInset : 0,
                    nextLoc.hasEdge(Edge.left) ? smallCornerInset : 0
                    ])
            }
       
            if (avoidRects && avoidRects.some(function(element) {
                return element.intersectsWithRect(drawRect);
            }))
            {
                // dlog(`${debugDesc(avoidRect)} intersects with ${debugDesc(drawRect)}; skipping`);
                return { stickerImagesUnique, borderCounter, smallUserPhotoForBorder };
            }
            
            dlog(`nextPoint: [${ArrayUtil.x(nextPoint)}, ${ArrayUtil.y(nextPoint)}]; x: ${drawRect.x}; y: ${drawRect.y}`)
        
            if (cyoDesign.debugOptions.drawBgs) { 
                ContentRendererUtils.debugAddBg(rotCanvas, ContentRendererUtils.debugBgColor2);
            }

            const theCtx = isUserImage ? photoStickersCtx : stickersCtx;

            ContentRendererUtils.placeImage(theCtx, 
                rotCanvas,
                drawRect,
                drawOutline);
        
            borderCounter += 1;
        }

        return { stickerImagesUnique, borderCounter, smallUserPhotoForBorder };
    }
    
    // Draw the user img into the given corner.
    static drawSingleCornerUserImage(ctx, userImgVariant, cornerLoc, device, canvasWidth, canvasHeight, cyoDesign) {
        const product = device.productCategory;
        
        dlog(">> _drawSingleCornerUserImage");
        
        // Get insets, which determine areas outside the mask that can't be drawn in
        const safeAreaInsets = device.safeAreaInsets;
        const croppedCanvasWidth = canvasWidth - ArrayUtil.left(safeAreaInsets) - ArrayUtil.right(safeAreaInsets);

        // Get corner padding, which limits the width of image
        const padding = renderSettings(product, "cornerUserImagePadding");
                
        // Calculate corner insets, which determine how much further the corner image gets pulled toward its edge
        const cornerInsetRatio = renderSettings(product, "cornerUserImageInsetRatio");        
        const cornerInsets = [cornerInsetRatio * canvasWidth, cornerInsetRatio * canvasHeight];
        
        // Negate rotationDegrees if necessary and convert to radians.
        var rotationAngle;
        if (cyoDesign.debugOptions.disableRotations) {
            rotationAngle = 0;
        } else {
            const rotationDegrees = renderSettings(product, "cornerStickerRotationDeg");
            if (cornerLoc.hasEdges([Edge.top, Edge.right])) {
                rotationAngle = (rotationDegrees + 180) * (Math.PI / 180);
            } else if (cornerLoc.hasEdges([Edge.bottom, Edge.right])) {
                rotationAngle = -rotationDegrees * (Math.PI / 180);
            } else if (cornerLoc.hasEdges([Edge.bottom, Edge.left])) {
                rotationAngle = rotationDegrees * (Math.PI / 180);
            } else if (cornerLoc.hasEdges([Edge.top, Edge.left])) {
                rotationAngle = (rotationDegrees - 90 + 180) * (Math.PI / 180);
            }
        }
        
        var rotCanvas = imgUtil.rotatedCanvas(userImgVariant, rotationAngle);
        
        // We get better placement if we crop out the new transparent rows/cols (at the cost of some performance)
        rotCanvas = imgUtil.cropTransparentImage(rotCanvas);
        
        if (cyoDesign.debugOptions.drawBgs) { 
            ContentRendererUtils.debugAddBg(rotCanvas, ContentRendererUtils.debugBgColor1);
        }
        
        // Get padding
        const paddingRatio = cornerLoc.hasEdge(Edge.left)
            ? ArrayUtil.right(padding) // image is being placed in lower left corner; use right padding
            : ArrayUtil.left(padding); // image is bring placed in lower right corner; use left padding
        const paddingPx = paddingRatio * canvasWidth;

        // Width of placed image = the safe area (croppedCanvasWidth) - padding - corner insets
        const drawRect = new Rect();
        drawRect.width = croppedCanvasWidth - paddingPx + ArrayUtil.x(cornerInsets);
        const scale = drawRect.width / rotCanvas.width;
        drawRect.height = rotCanvas.height * scale;
        
        // Calculate x, y placement
        drawRect.x = cornerLoc.hasEdge(Edge.left)
            ? cornerLoc.x
            : cornerLoc.x - drawRect.width;
        drawRect.y = cornerLoc.hasEdge(Edge.top)
            ? cornerLoc.y
            : cornerLoc.y - drawRect.height;
        
        if (product == Product.grip) {
            // For grip it's better to move back into the image by half
            cornerLoc.hasEdge(Edge.right) ? drawRect.x += drawRect.width/2 : drawRect.x -= drawRect.width / 2;
            drawRect.y += drawRect.height/2;
        }
                
        // Apply additional insets
        drawRect.x += cornerLoc.hasEdge(Edge.left) ? ArrayUtil.x(cornerInsets) : -ArrayUtil.x(cornerInsets);
        drawRect.y -= cornerLoc.hasEdge(Edge.bottom) ? ArrayUtil.y(cornerInsets) : -ArrayUtil.y(cornerInsets);
        
        dlog(`_drawSingleCornerUserImage: x:${drawRect.x}; y:${drawRect.y}; w:${drawRect.width}; h:${drawRect.height})`)
                    
        ContentRendererUtils.placeImage(ctx, 
            rotCanvas,
            drawRect,
            cyoDesign.subjectOutlines,
            cyoDesign
        );
        
        return drawRect;
    }
    
    static drawImgCornerAndStickerBorderOrPattern(
        stickersCtx,
        photoStickersCtx,
        userImgVariant,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        stickerImagesUnique,
        popSticker,
        randomState = null
    ) {
        dlog(">> _drawImgCornerAndStickerBorderOrPattern");

        const product = device.productCategory;
        // For corner locations, we don't need sticker sizing calculations
        const borderLocs = BorderLoc.getLocs(device, canvasWidth, canvasHeight);
        const cornerLoc = BorderLoc.randomBottomCornerLoc(borderLocs, null, `_drawImgCornerAndStickerBorderOrPattern-${product}`, randomState);
                
        let borderCounter = 0;
        let smallUserPhotoForBorder = null; // _drawNextBorderSticker caches this
        
        borderLocs.forEach((nextLoc) => {
            if (stickerImagesUnique.length == 0) {
                // re-populate unique sticker images
                stickerImagesUnique = stickerImages.concat();
            }
        
            // Custom avoidance for case and wallet
            if (product == Product.case || product == Product.wallet) {
                if (placements.stickers.border) {
                    if (arraysUnorderedEqual(nextLoc.edges, cornerLoc.edges)) {
                        return;
                    }
                }
            }
            const result = ContentRendererBorderCorner.drawNextBorderSticker(
                stickersCtx, photoStickersCtx, userImgVariant, nextLoc, [], device, canvasWidth, canvasHeight, cyoDesign, placements, stickerImagesUnique, borderCounter, smallUserPhotoForBorder, popSticker, randomState
            );
            stickerImagesUnique = result.stickerImagesUnique;
            borderCounter = result.borderCounter;
            smallUserPhotoForBorder = result.smallUserPhotoForBorder;
        });
                
        ContentRendererBorderCorner.drawSingleCornerUserImage(photoStickersCtx, userImgVariant, cornerLoc, device, canvasWidth, canvasHeight, cyoDesign);

        return { stickerImagesUnique, smallUserPhotoForBorder };
    }
    
    static drawImgAndStickerCorners(
        stickersCtx,
        photoStickersCtx,
        userImgVariant,
        device, 
        canvasWidth, 
        canvasHeight, 
        cyoDesign, 
        stickerImages, 
        placements,
        popSticker,
        randomState = null
    ) {
        const product = device.productCategory;
        const croppedCanvasRect = croppedDrawRect(device, canvasWidth, canvasHeight);

        var imgCornerLoc, stickerCornerLoc;

        dlog(">> _drawImgAndStickerCorners");

        let cornerLocs = BorderLoc.getCornerLocs(device, canvasWidth, canvasHeight, true /* ignore margins */);
        if (product == Product.grip) {
            cornerLocs = cornerLocs.filter(loc => loc.hasEdge(Edge.bottom));
            if (RandomState.togglingRandomBool("_drawImgAndStickerCorners-grip", randomState)) {
                imgCornerLoc = cornerLocs[0];
                stickerCornerLoc = cornerLocs[1];
            } else {
                imgCornerLoc = cornerLocs[1];
                stickerCornerLoc = cornerLocs[0];
            }
        } else {        
            // For corner locations, we don't need sticker sizing calculations
            imgCornerLoc = BorderLoc.randomBottomCornerLoc(cornerLocs, null, `_drawImgAndStickerCorners-${product}`, randomState);
            stickerCornerLoc = BorderLoc.randomBottomCornerLoc(cornerLocs, imgCornerLoc.edges, null);
        }
                
        function placeSticker() {
            const stickerSet = cyoDesign.stickerSet;
            const cornerStickerWidthRatio = renderSettings(product, "cornerLargeStickerWidthRatio");
            const [stickerIdx, stickerImg] = popSticker(sticker => sticker.width * sticker.height >= MIN_CORNER_STICKER_RESOLUTION);
            
            // Calculate corner insets
            const cornerInsetRatio = renderSettings(device.productCategory, "cornerLargeStickerInsetRatio");
            const cornerInsetAmt = cornerInsetRatio * croppedCanvasRect.width;

            var insets = [cornerInsetAmt, cornerInsetAmt];
            
            // #172: Ability to mark some stickers as having 0 inset in the corners. We want these to appear to "grow" out of the bottom corner, so remove the inset.
            // (grip already has a negative inset, so no need there)
            if (product != Product.grip) {
                const ignoreLargeCornerInsetIdxs = stickerSet.ignoreLargeCornerInsetIdxs;
                if (ignoreLargeCornerInsetIdxs && ignoreLargeCornerInsetIdxs.includes(stickerIdx)) {
                    insets = [0, 0];
                }
            }
            
            const width = cornerStickerWidthRatio * croppedCanvasRect.width - ArrayUtil.x(insets)/2;

            ContentRendererBorderCorner.placeAsStickerInCorner(stickersCtx,
                stickerImg,
                stickerCornerLoc,
                renderSettings(product, "cornerStickerRotationDeg"),
                width,
                insets,
                cyoDesign);
        }
        
        function placeUserImage() {
            // Use the same positioning logic as when user image is alone (no stickers)
            // This ensures consistent placement regardless of whether stickers are present
            ContentRendererBorderCorner.drawSingleCornerUserImage(
                photoStickersCtx,
                userImgVariant,
                imgCornerLoc,
                device,
                canvasWidth,
                canvasHeight,
                cyoDesign
            );
        }
        
        // Altan: user image goes on top if bg removed, otherwise sticker goes on top (in case they overlap)
        const userImageHasTransparency = userImgVariant && imgUtil.isTransparent(userImgVariant);
        if (userImageHasTransparency) {
            placeSticker();
            placeUserImage();
        } else {
            placeUserImage();
            placeSticker();
        }

        return { stickerImagesUnique: [] };
    }
    
    static placeAsStickerInCorner(ctx, imgOrCanvas, borderLoc, rotationDegrees, width, insets, cyoDesign) {
        // Negate rotationDegrees if necessary and convert to radians.
        const rotationAngle = (cyoDesign.debugOptions.disableRotations || cyoDesign.stickerSet?.disableRotations)
            ? 0
            : (borderLoc.hasEdge(Edge.left)
                ? rotationDegrees * (Math.PI / 180)
                : -rotationDegrees * (Math.PI / 180));
        
        var rotCanvas = imgUtil.rotatedCanvas(imgOrCanvas, rotationAngle);
        // We get better placement if we crop out the new transparent rows/cols (at the cost of some performance)
        rotCanvas = imgUtil.cropTransparentImage(rotCanvas);
    
        if (cyoDesign.debugOptions.drawBgs) { 
            ContentRendererUtils.debugAddBg(rotCanvas, ContentRendererUtils.debugBgColor2);
        }
    
        const drawRect = new Rect();
        const scale = width / rotCanvas.width;
        drawRect.width = rotCanvas.width * scale;
        drawRect.height = rotCanvas.height * scale;
    
        drawRect.x = borderLoc.x;
        if (borderLoc.hasEdge(Edge.right)) {
          drawRect.x -= drawRect.width;
        }
        drawRect.y = borderLoc.y - drawRect.height;
                        
        // Apply additional insets
        if (insets) {
            if (borderLoc.hasEdge(Edge.left)) {
                drawRect.x += ArrayUtil.x(insets);
            } else {
                drawRect.x -= ArrayUtil.x(insets);
            }
            if (borderLoc.hasEdge(Edge.top)) {
                drawRect.y += ArrayUtil.y(insets);
            } else {
                drawRect.y -= ArrayUtil.y(insets);
            }
        }
                            
        ContentRendererUtils.placeImage(ctx, 
            rotCanvas,
            drawRect,
            cyoDesign.subjectOutlines,
            cyoDesign
        );
    }
}